"use client";
import React, { useMemo, useState, useEffect } from "react";
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { Home, Briefcase, Clock, Users, Truck, Building2, Kanban, ClipboardList, FileText, BarChart3, Bell, LifeBuoy, Settings, Search, Filter as FilterIcon, MessageSquare, Lock, Map as MapIcon, ShieldCheck, Sparkles } from 'lucide-react';
const MapCanvas = dynamic(() => import('./MapCanvas'), { ssr: false });
import { useRouter } from 'next/navigation';
import Ably from 'ably';
import { useSession } from 'next-auth/react';
import { hasFleetAccess, canManageFleet } from '@/lib/fleet-access';

type Item = { id: string; name: string; meta: string; starred?: boolean };

const MOCK_ITEMS: Item[] = Array.from({ length: 12 }).map((_, i) => ({
  id: `obj-${i + 1}`,
  name: `[String] [Name]`,
  meta: `Metadata-${(i % 3) + 1} • Created by: [Link w/ access]`,
}));

export default function FleetDashboard() {
  const router = useRouter();
  const { data: session, status } = useSession();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<string[]>(["obj-2", "obj-4", "obj-7"]);
  const [starred, setStarred] = useState<Record<string, boolean>>({ "obj-3": true, "obj-8": true });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const CHIP_LABELS: string[] = ["[String]","[Name]","[Live]","[New]"];
  const [navSort, setNavSort] = useState<'DEFAULT'|'AZ'>('DEFAULT');
  const [showNavFilter, setShowNavFilter] = useState(false);
  const [orgName, setOrgName] = useState<string>('Your company');
  const [navGroups, setNavGroups] = useState<Record<string, boolean>>({
    Dashboard: true,
    Jobs: true,
    Drivers: true,
    Vehicles: true,
    Customers: true,
    Chats: true,
    Dispatch: true,
    Assignments: true,
    Billing: true,
    Analytics: true,
    Alerts: true,
    Support: true,
    Settings: true,
  });

  // Authentication check - redirect immediately if not authenticated or not authorized
  useEffect(() => {
    if (status === 'loading') return; // Wait for session to load
    
    if (!session) {
      router.push('/fleet/login');
      return;
    }

    if (!hasFleetAccess(session.user)) {
      router.push('/unauthorized');
      return;
    }
  }, [session, status, router]);

  // Don't render anything until we've checked authentication
  if (status === 'loading' || !session) {
    return null;
  }

  if (!hasFleetAccess(session.user)) {
    return null; // Will redirect via useEffect
  }
  
  const canManage = canManageFleet(session.user);

  const labelToGroup = (label: string): string => {
    switch (label) {
      case 'Dashboard': return 'Dashboard';
      case 'Active Jobs':
      case 'New Jobs':
      case 'Completed Jobs':
      case 'Post Job':
      case 'Job Marketplace': return 'Jobs';
      case 'Drivers': return 'Drivers';
      case 'Vehicles': return 'Vehicles';
      case 'Customer Contacts': return 'Customers';
      case 'Chats': return 'Chats';
      case 'Dispatch Board': return 'Dispatch';
      case 'Assignments': return 'Assignments';
      case 'Billing & Invoices': return 'Billing';
      case 'Analytics': return 'Analytics';
      case 'Alerts & Incidents': return 'Alerts';
      case 'Support': return 'Support';
      case 'Settings': return 'Settings';
      default: return 'Dashboard';
    }
  };

  // Load business/organisation name for sidebar header (only for fleet managers/admins)
  useEffect(() => {
    if (!canManage) return; // Skip if not manager/admin (viewers/drivers don't need this)
    
    (async () => {
      try {
        const r = await fetch('/api/web/user');
        const j = await r.json();
        if (r.ok && j?.user) {
          const name = j.user.businessName || j.user.name || 'Your company';
          setOrgName(String(name));
        }
      } catch {}
    })();
  }, [canManage]);

  // Mock drivers shown as pins with initials and type
// Placeholder map driver data removed; MapCanvas now renders only real-time/real data

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return MOCK_ITEMS.filter((it) => !q || it.name.toLowerCase().includes(q) || it.meta.toLowerCase().includes(q));
  }, [query]);

  const toggleSelect = (id: string) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleStar = (id: string) => {
    setStarred((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const saveBulk = () => {
    setBulkOpen(false);
    setToast("Updated Selected Objects");
    setTimeout(() => setToast(null), 2200);
  };

  const [activeDriver, setActiveDriver] = useState<string | null>(null);
  const [showActive, setShowActive] = useState(false);
  const [showPostHover, setShowPostHover] = useState(false);
  const [activeLoading, setActiveLoading] = useState(false);
  const [activeDetail, setActiveDetail] = useState<{ jobId: string; jobType: string; budget?: number|null; description?: string|null; from?: any; to?: any; isUrgent?: boolean; fragile?: boolean; carMake?: string|null; carModel?: string|null; carReg?: string|null; breakdownReason?: string|null; driverName?: string|null } | null>(null);
  const [activeEtaMin, setActiveEtaMin] = useState<number | null>(null);
  const [activeDrivers, setActiveDrivers] = useState<Array<{ driverId: string; driverName: string; jobId: string; type: string }>>([]);
  const [sub, setSub] = useState<any>(null);
  const [subLoading, setSubLoading] = useState<boolean>(true);
  const [showPlanOnboard, setShowPlanOnboard] = useState<boolean>(false);
  const [planCycle, setPlanCycle] = useState<'monthly'|'yearly'>('yearly');
  const [planSubmitting, setPlanSubmitting] = useState<string>('');

  React.useEffect(() => {
    if (!canManage) return; // Skip if not authorized
    if (!activeDriver) { setActiveDetail(null); return; }
    
    setActiveLoading(true);
    (async () => {
      try {
        // 1) Find recent jobs for this driver
        const listUrl = new URL('/api/fleet/jobs', window.location.origin);
        listUrl.searchParams.set('driverId', activeDriver);
        listUrl.searchParams.set('limit', '5');
        const lr = await fetch(listUrl.toString());
        const lj = await lr.json();
        const items: any[] = (lr.ok && Array.isArray(lj?.jobs)) ? lj.jobs : [];
        if (!items.length) { setActiveDetail(null); return; }
        // Prefer IN_PROGRESS, then ACCEPTED, else first
        const pick = items.find(j=> j.status==='IN_PROGRESS') || items.find(j=> j.status==='ACCEPTED') || items[0];
        const driverName = pick?.driver?.name || null;
        // 2) Load full job details
        const jr = await fetch(`/api/jobs/${pick.id}`);
        const jj = await jr.json();
        const job = (jr.ok && (jj?.job || jj)) ? (jj.job || jj) : pick;
        // Address helpers
        const addrToString = (a: any): string => {
          if (!a) return '';
          if (typeof a === 'string') return a;
          return a.address || a.formatted_address || [a.line1, a.city, a.postcode].filter(Boolean).join(', ');
        };
        setActiveDetail({
          jobId: String(pick.id),
          jobType: String(job.type || pick.type || ''),
          budget: (job.budget ?? pick.budget) ?? null,
          description: job.description || pick.description || null,
          from: job.fromAddress || pick.fromAddress,
          to: job.toAddress || pick.toAddress,
          isUrgent: !!(job.isUrgent ?? pick.isUrgent),
          fragile: !!(job.fragile ?? pick.fragile),
          carMake: job.carMake ?? null,
          carModel: job.carModel ?? null,
          carReg: job.carReg ?? null,
          breakdownReason: job.breakdownReason ?? null,
          driverName,
        });
        setActiveEtaMin(null);
      } catch {
        setActiveDetail(null);
      } finally {
        setActiveLoading(false);
      }
    })();
  }, [activeDriver, canManage]);

  // Live ETA updates based on driver location events
  React.useEffect(() => {
    if (!activeDriver || !activeDetail) return;
    let client: Ably.Realtime | null = null;
    const toCoords = (() => {
      const t: any = activeDetail.to;
      const lat = (t && typeof t === 'object') ? (t.lat ?? t.latitude ?? t.toLat) : undefined;
      const lng = (t && typeof t === 'object') ? (t.lng ?? t.longitude ?? t.toLng) : undefined;
      return (typeof lat === 'number' && typeof lng === 'number') ? { lat, lng } : null;
    })();
    const haversineKm = (lat1: number, lon1: number, lat2: number, lon2: number) => {
      const toRad = (v: number) => (v * Math.PI) / 180;
      const R = 6371;
      const dLat = toRad(lat2 - lat1);
      const dLon = toRad(lon2 - lon1);
      const a = Math.sin(dLat/2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon/2) ** 2;
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
    };
    const computeEta = (curLat: number, curLng: number) => {
      if (!toCoords) { setActiveEtaMin(null); return; }
      const km = haversineKm(curLat, curLng, toCoords.lat, toCoords.lng);
      if (!isFinite(km)) { setActiveEtaMin(null); return; }
      const t = (activeDetail.jobType || '').toUpperCase();
      const speedKmH = (t === 'LOGISTICS' || t === 'PALLETE') ? 60 : 45;
      const mins = Math.max(1, Math.round((km / speedKmH) * 60));
      setActiveEtaMin(mins);
    };
    (async () => {
      try {
        client = new Ably.Realtime({ authUrl: '/api/ably/token' } as any);
        const ch = client.channels.get('drivers:locations');
        const handler = (msg: Ably.Types.Message) => {
          const d: any = msg.data || {};
          if (d.driverId !== activeDriver) return;
          if (typeof d.lat === 'number' && typeof d.lng === 'number') computeEta(d.lat, d.lng);
        };
        ch.subscribe('location', handler);
      } catch {}
    })();
    return () => { try { client?.close(); } catch {} };
  }, [activeDriver, activeDetail?.jobType, activeDetail?.to]);

  // Load active drivers list (only for fleet managers)
  React.useEffect(() => {
    if (!canManage) return; // Skip if not authorized
    
    let client: Ably.Realtime | null = null;
    const loadActive = async () => {
      try {
        const url = new URL('/api/fleet/jobs', window.location.origin);
        url.searchParams.set('status', 'IN_PROGRESS');
        url.searchParams.set('limit', '100');
        const r = await fetch(url.toString());
        const j = await r.json();
        const jobs: any[] = (r.ok && Array.isArray(j?.jobs)) ? j.jobs : [];
        const byDriver: Record<string, { driverId: string; driverName: string; jobId: string; type: string }> = {};
        for (const job of jobs) {
          const did = job?.driver?.id;
          if (!did) continue;
          if (!byDriver[did]) byDriver[did] = { driverId: did, driverName: job.driver.name || did, jobId: job.id, type: job.type };
        }
        setActiveDrivers(Object.values(byDriver));
      } catch { setActiveDrivers([]); }
    };
    loadActive();
    (async () => {
      try {
        client = new Ably.Realtime({ authUrl: '/api/ably/token' } as any);
        const ch1 = client.channels.get('fleet:jobs');
        const ch2 = client.channels.get('drivers:locations');
        const handler = () => loadActive();
        ch1.subscribe('job.status.updated', handler);
        ch2.subscribe('location', handler);
      } catch {}
    })();
    const id = setInterval(loadActive, 60 * 1000);
    return () => { try { clearInterval(id); client?.close(); } catch {} };
  }, [canManage]);

  // Load subscription status (only for fleet managers)
  React.useEffect(() => {
    if (!canManage) { setSubLoading(false); return; } // Skip if not authorized
    
    (async () => { 
      try { 
        const r = await fetch('/api/fleet/billing/status'); 
        const j = await r.json(); 
        if (r.ok) setSub(j.subscription || null); 
      } catch { 
        setSub(null); 
      } 
      setSubLoading(false); 
    })();
  }, [canManage]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!subLoading) {
      try {
        const dismissed = localStorage.getItem('pricingOnboardDismissed') === '1';
        if (!sub?.subscriptionStatus && !dismissed) setShowPlanOnboard(true);
      } catch { if (!sub?.subscriptionStatus) setShowPlanOnboard(true); }
    }
  }, [subLoading, sub?.subscriptionStatus]);

  const subscribePlan = async (lookup: string) => {
    try {
      setPlanSubmitting(lookup);
      const r = await fetch('/api/fleet/billing/subscribe', { method:'POST', headers:{ 'Content-Type':'application/json' }, body: JSON.stringify({ planLookupKey: lookup }) });
      const j = await r.json();
      if (!r.ok || !j?.url) throw new Error(j?.error || 'Failed');
      window.location.href = j.url as string;
    } catch (e) { alert((e as any)?.message || 'Failed'); } finally { setPlanSubmitting(''); }
  };

  return (
    <div className="grid grid-cols-[320px,1fr] h-[calc(100vh-48px)] overflow-hidden" style={{ background: 'var(--fleet-bg)' }}>
      {/* Left rail */}
      <aside className="fleet-sidebar p-2 flex flex-col gap-2 overflow-auto" style={{ background: 'var(--fleet-panel)', borderRight: '1px solid var(--fleet-border)' }}>
        <div className="flex items-center justify-between text-[11px] px-2 py-2" style={{ color: 'var(--fleet-muted)', borderBottom: '1px solid var(--fleet-border)' }}>
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded" style={{ background: 'var(--fleet-accent)' }} />
            <span className="font-medium" style={{ color: 'var(--fleet-text)' }}>{orgName}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-zinc-500">View</span>
            <select value={navSort} onChange={(e)=> setNavSort(e.target.value as any)} className="text-[11px] px-1 py-0.5 rounded"
              style={{ background:'var(--fleet-subtle)', border:'1px solid var(--fleet-border)', color:'var(--fleet-text)' }}>
              <option value="DEFAULT">Default</option>
              <option value="AZ">A–Z</option>
            </select>
        </div>
        </div>
        <div className="px-2 py-2 flex items-center gap-2 relative">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search pages…"
            className="flex-1 rounded pl-7 pr-2 py-1 text-[12px] placeholder-zinc-500 relative"
            style={{ backgroundColor: 'var(--fleet-subtle)', border: '1px solid var(--fleet-border)' }}
          />
          <button onClick={()=> setShowNavFilter(v=> !v)} className="px-2 py-1 text-[11px] rounded inline-flex items-center gap-1" style={{ background:'rgba(255,255,255,0.06)', border:'1px solid var(--fleet-border)', color:'var(--fleet-text)' }}>
            <FilterIcon size={12} /> Filter
          </button>
          {showNavFilter && (
            <div className="absolute right-2 top-full mt-2 w-64 rounded-lg z-50" style={{ background:'var(--fleet-panel)', border:'1px solid var(--fleet-border)' }}>
              <div className="px-3 py-2 border-b text-[12px] flex items-center justify-between" style={{ borderColor:'var(--fleet-border)', color:'var(--fleet-muted)' }}>
                <div>Show in sidebar</div>
                <div className="flex items-center gap-1">
                  <button className="px-1 py-0.5 text-[11px] rounded border" style={{ border:'1px solid var(--fleet-border)' }} onClick={()=> setNavGroups((p)=> Object.fromEntries(Object.keys(p).map(k=> [k, true])) as any)}>All</button>
                  <button className="px-1 py-0.5 text-[11px] rounded border" style={{ border:'1px solid var(--fleet-border)' }} onClick={()=> setNavGroups((p)=> Object.fromEntries(Object.keys(p).map(k=> [k, false])) as any)}>None</button>
                </div>
              </div>
              <div className="p-2 grid grid-cols-2 gap-1 text-[12px]" style={{ color:'var(--fleet-text)' }}>
                {Object.keys(navGroups).map((g)=> (
                  <label key={g} className="flex items-center gap-2 px-1 py-0.5 rounded hover:bg-black/5">
                    <input type="checkbox" checked={!!navGroups[g]} onChange={(e)=> setNavGroups((p)=> ({ ...p, [g]: e.target.checked }))} />
                    <span>{g}</span>
                  </label>
                ))}
              </div>
              <div className="px-3 py-2 border-t flex justify-end" style={{ borderColor:'var(--fleet-border)' }}>
                <button className="px-2 py-1 text-[12px] rounded border" style={{ border:'1px solid var(--fleet-border)' }} onClick={()=> setShowNavFilter(false)}>Close</button>
              </div>
            </div>
          )}
        </div>
        <div className="px-2 text-[10px] uppercase tracking-wider" style={{ color: 'var(--fleet-muted)' }}>Pages</div>
        <nav className="flex-1 overflow-auto px-2 py-1 space-y-1">
          {(() => {
            const ALL = [
            'Dashboard',
            'Active Jobs',
              'Job Marketplace',
            'New Jobs',
            'Completed Jobs',
              'Post Job',
            'Live Ops',
            'Drivers',
            'Vehicles',
            'Customer Contacts',
              'Chats',
            'Dispatch Board',
            'Assignments',
            'Strix AI',
            'AI Assistant',
              'Quotes',
            'Compliance Vault',
            'Billing & Invoices',
            'Analytics',
            'Alerts & Incidents',
            'Support',
            'Settings',
            ];
            const q = query.trim().toLowerCase();
            let list = ALL.filter((label) => {
              const group = labelToGroup(label);
              const enabled = navGroups[group] !== false;
              if (!enabled) return false;
              return !q || label.toLowerCase().includes(q);
            });
            if (navSort === 'AZ') list = [...list].sort((a,b)=> a.localeCompare(b));
            return list;
          })().map((label) => {
            const href = label === 'Active Jobs' ? '/fleet/jobs?status=ACCEPTED'
              : label === 'New Jobs' ? '/fleet/jobs?status=PENDING'
              : label === 'Completed Jobs' ? '/fleet/jobs?status=COMPLETED'
              : label === 'Dashboard' ? '/fleet'
              : label === 'Post Job' ? '/fleet/post-job'
              : label === 'Drivers' ? '/fleet/drivers'
              : label === 'Vehicles' ? '/fleet/vehicles'
              : label === 'Customer Contacts' ? '/fleet/customers'
              : label === 'Chats' ? '/fleet/chats'
              : label === 'Dispatch Board' ? '/fleet/dispatch'
              : label === 'Assignments' ? '/fleet/assignments'
              : label === 'Strix AI' ? '/fleet/ai/plans'
              : label === 'AI Assistant' ? '/fleet/ai'
              : label === 'Quotes' ? '/fleet/quotes'
              : label === 'Billing & Invoices' ? '/fleet/billing'
              : label === 'Job Marketplace' ? '/fleet/job-marketplace'
              : label === 'Live Ops' ? '/fleet/live-ops'
              : label === 'Compliance Vault' ? '/fleet/compliance/vault'
              : label === 'Analytics' ? '/fleet/analytics'
              : label === 'Alerts & Incidents' ? '/fleet/alerts'
              : label === 'Support' ? '/fleet/support'
              : label === 'Settings' ? '/fleet/settings'
              : undefined;
            const Icon = label === 'Dashboard' ? Home
              : label === 'Active Jobs' ? Briefcase
              : label === 'New Jobs' ? Clock
              : label === 'Completed Jobs' ? ClipboardList
              : label === 'Post Job' ? ClipboardList
              : label === 'Live Ops' ? MapIcon
              : label === 'Drivers' ? Users
              : label === 'Vehicles' ? Truck
              : label === 'Customer Contacts' ? Building2
              : label === 'Chats' ? MessageSquare
              : label === 'Dispatch Board' ? Kanban
              : label === 'Assignments' ? ClipboardList
              : label === 'Strix AI' ? Sparkles
              : label === 'AI Assistant' ? MessageSquare
              : label === 'Quotes' ? ClipboardList
              : label === 'Billing & Invoices' ? FileText
              : label === 'Job Marketplace' ? Truck
              : label === 'Analytics' ? BarChart3
              : label === 'Alerts & Incidents' ? Bell
              : label === 'Support' ? LifeBuoy
              : label === 'Compliance Vault' ? ShieldCheck
              : Settings;
            const isFleetAi = label === 'Strix AI';
            return href ? (
              <Link key={label} href={href} className="block w-full">
                <span className="nav-pill block w-full text-left px-3 py-2 rounded text-[12px] hover:opacity-90 inline-flex items-center gap-2" style={{ background: isFleetAi ? 'linear-gradient(90deg, rgba(99,102,241,0.22), rgba(34,197,94,0.18))' : 'rgba(255,255,255,0.04)', border: '1px solid var(--fleet-border)', color: isFleetAi ? '#ffffff' : 'var(--fleet-text)' }}>
                  <Icon size={14} /> {label}
                </span>
              </Link>
            ) : (
              <button key={label} className="nav-pill w-full text-left px-3 py-2 rounded text-[12px] hover:opacity-90 inline-flex items-center gap-2" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--fleet-border)', color: 'var(--fleet-text)' }}>
                <Settings size={14} /> {label}
            </button>
            );
          })}
        </nav>
        <div className="px-2 py-2 space-y-2" style={{ borderTop: '1px solid var(--fleet-border)' }}>
          <div className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--fleet-muted)' }}>Quick Actions</div>
          <div className="flex items-center gap-2 pt-1">
            <button 
              onClick={() => window.location.href = '/fleet/post-job'}
              className="px-2 py-1 text-[12px] rounded" 
              style={{ backgroundColor: 'var(--fleet-accent)', color: '#000' }}
            >
              Post Job
            </button>
            <Link href="/fleet/jobs?status=PENDING" className="qa-secondary px-2 py-1 text-[12px] rounded" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid var(--fleet-border)', color: 'var(--fleet-text)' }}>
              View Jobs
            </Link>
            <Link href="/fleet/drivers/invite" className="qa-secondary px-2 py-1 text-[12px] rounded" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid var(--fleet-border)', color: 'var(--fleet-text)' }}>
              Invite Driver
            </Link>
          </div>
        </div>
        <div className="px-2 py-2">
          <div className="text-[11px] flex items-center gap-2" style={{ color:'var(--fleet-muted)' }}>
            <span>★</span>
            <Link href="/fleet/privacy" className="hover:underline" style={{ color:'var(--fleet-text)' }}>Privacy</Link>
            <span>•</span>
            <Link href="/fleet/terms" className="hover:underline" style={{ color:'var(--fleet-text)' }}>Terms</Link>
          </div>
        </div>
      </aside>

      {/* Main canvas */}
      <main className="relative h-[calc(100vh-48px)]" style={{ background: 'var(--fleet-bg)' }}>
        {/* toast */}
        {toast && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 text-[12px]">
            <div className="px-3 py-1.5 rounded shadow" style={{ border: '1px solid rgba(16,185,129,0.6)', background: 'rgba(15,28,20,0.9)', color: 'var(--fleet-success)' }}>✓ {toast}</div>
          </div>
        )}
        {/* map canvas (Mapbox if token present, else gradient remains) */}
        <div className="absolute inset-0" style={{ background: 'var(--fleet-bg)', zIndex: 0 }} />
        <div className="absolute inset-0 h-full w-full" style={{ zIndex: 1 }}>
          <MapCanvas onDriverClick={(driverId: string) => setActiveDriver(driverId)} showDemoRoutes={!sub?.gpsTrackingEnabled} />
        </div>
        {/* Map lock overlay for non-Advanced: blur map with lock + Upgrade */}
        {(!subLoading && (!sub || !sub.gpsTrackingEnabled)) && (
          <div className="absolute inset-0 z-10 flex items-center justify-center">
            <div className="absolute inset-0" style={{ backdropFilter:'blur(3px)', background:'rgba(0,0,0,0.35)' }} />
            <div className="relative z-10 rounded-lg border px-5 py-4 text-center" style={{ borderColor:'var(--fleet-border)', background:'var(--fleet-panel)', color:'var(--fleet-text)' }}>
              <div className="mx-auto mb-2 h-10 w-10 rounded-full flex items-center justify-center" style={{ background:'rgba(255,255,255,0.06)', border:'1px solid var(--fleet-border)' }}>
                <Lock size={18} />
              </div>
              <div className="text-[13px] font-semibold">Live GPS tracking (Advanced)</div>
              <div className="text-[12px] mt-1" style={{ color:'var(--fleet-muted)' }}>Upgrade to unlock real-time driver locations on the map.</div>
              <div className="mt-3 flex items-center justify-center gap-2">
                <button className="px-3 py-1 text-[12px] rounded" style={{ background:'#f59e0b', color:'#000' }} onClick={()=> setShowPlanOnboard(true)}>Upgrade</button>
                <button className="px-3 py-1 text-[12px] rounded border" style={{ border:'1px solid var(--fleet-border)' }} onClick={()=> router.push('/fleet/billing')}>See plans</button>
              </div>
            </div>
          </div>
        )}

        {/* Pricing onboarding modal */}
        {showPlanOnboard && (
          <div className="fixed inset-0 z-[60] bg-black/60" onClick={()=>{}}>
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[960px] max-w-[95vw]" onClick={(e)=> e.stopPropagation()}>
              <div className="rounded-lg overflow-hidden" style={{ background:'var(--fleet-panel)', border:'1px solid var(--fleet-border)', color:'var(--fleet-text)' }}>
                <div className="p-4 border-b flex items-center justify-between" style={{ borderColor:'var(--fleet-border)' }}>
                  <div>
                    <div className="text-[12px]" style={{ color:'var(--fleet-muted)' }}>Choose a plan</div>
                    <div className="text-[16px] font-semibold">Welcome to Strix</div>
                  </div>
                  <div className="inline-flex items-center gap-1 text-[11px] rounded-full border p-0.5" style={{ borderColor:'var(--fleet-border)', background:'var(--fleet-subtle)' }}>
                    <button onClick={()=> setPlanCycle('monthly')} className={`px-3 py-1 rounded-full transition-colors`} style={{ background: planCycle==='monthly' ? 'var(--fleet-accent)' : 'transparent', color: planCycle==='monthly' ? '#000' : 'var(--fleet-muted)' }}>
                      {planCycle==='monthly' ? 'Monthly ✓' : 'Monthly'}
                    </button>
                    <button onClick={()=> setPlanCycle('yearly')} className={`px-3 py-1 rounded-full transition-colors`} style={{ background: planCycle==='yearly' ? 'var(--fleet-accent)' : 'transparent', color: planCycle==='yearly' ? '#000' : 'var(--fleet-muted)' }}>
                      {planCycle==='yearly' ? 'Yearly ✓' : 'Yearly'}
                    </button>
                  </div>
                </div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Starter */}
                  <div className="rounded border p-3 relative overflow-hidden" style={{ borderColor:'var(--fleet-border)', background:'linear-gradient(180deg, rgba(255,255,255,0.02), rgba(0,0,0,0))', boxShadow:'0 10px 30px rgba(0,0,0,0.15)' }}>
                    <div className="text-[13px] font-semibold">Strix Starter</div>
                    <div className="text-[11px] mt-1" style={{ color:'var(--fleet-muted)' }}>Up to 3 drivers • Manual assignment • Team analytics</div>
                    {planCycle==='yearly' && (
                      <div className="mt-2 inline-flex items-center gap-2 text-[10px] px-2 py-0.5 rounded-full" style={{ background:'rgba(34,197,94,0.12)', border:'1px solid rgba(34,197,94,0.4)', color:'#22c55e' }}>Save 15% (2 months free)</div>
                    )}
                    <div className="mt-3 flex items-center gap-2">
                      <div className="text-[20px] font-semibold">{planCycle==='monthly' ? '£49.99/mo' : '£509/yr'}</div>
                    </div>
                    <div className="mt-3 space-y-1 text-[11px]" style={{ color:'var(--fleet-text)' }}>
                      <div className="font-medium" style={{ color:'var(--fleet-muted)' }}>Team Management</div>
                      <div className="mt-1 space-y-1">
                        {['Add up to 3 drivers','Control job visibility','Manage distribution'].map((t,i)=> (
                          <div key={`ob-st-${i}`} className="flex items-start gap-2"><svg className="h-3 w-3 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ color:'#22c55e' }}><path d="M20 6L9 17l-5-5"/></svg><span>{t}</span></div>
                        ))}
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <button className="px-3 py-1 text-[12px] rounded w-full md:w-auto" style={{ background:'var(--fleet-accent)', color:'#000' }} disabled={planSubmitting=== (planCycle==='monthly'?'starter_monthly':'starter_yearly')} onClick={()=> subscribePlan(planCycle==='monthly'?'starter_monthly':'starter_yearly')}>{planSubmitting=== (planCycle==='monthly'?'starter_monthly':'starter_yearly') ? 'Loading…' : (planCycle==='monthly' ? 'Choose Starter (Monthly)' : 'Choose Starter (Yearly)')}</button>
                    </div>
                  </div>
                  {/* Advanced */}
                  <div className="relative rounded border p-3 overflow-hidden" style={{ borderColor:'var(--fleet-border)', background:'linear-gradient(180deg, rgba(99,102,241,0.08), rgba(0,0,0,0))', boxShadow:'0 14px 36px rgba(99,102,241,0.18)' }}>
                    <div className="absolute right-3 top-3 text-[10px] px-2 py-0.5 rounded-full" style={{ background:'rgba(99,102,241,0.16)', border:'1px solid rgba(99,102,241,0.4)', color:'#a5b4fc' }}>Most popular</div>
                    <div className="text-[13px] font-semibold">Strix Advanced</div>
                    <div className="text-[11px] mt-1" style={{ color:'var(--fleet-muted)' }}>Unlimited drivers • Everything in Starter • Live GPS</div>
                    {planCycle==='yearly' && (
                      <div className="mt-2 inline-flex items-center gap-2 text-[10px] px-2 py-0.5 rounded-full" style={{ background:'rgba(34,197,94,0.12)', border:'1px solid rgba(34,197,94,0.4)', color:'#22c55e' }}>Save 25% (3 months free)</div>
                    )}
                    <div className="mt-3 flex items-center gap-2">
                      <div className="text-[20px] font-semibold">{planCycle==='monthly' ? '£99/mo' : '£891/yr'}</div>
                    </div>
                    <div className="mt-3 space-y-1 text-[11px]" style={{ color:'var(--fleet-text)' }}>
                      <div className="font-medium" style={{ color:'var(--fleet-muted)' }}>Live GPS Tracking (Advanced Only)</div>
                      <div className="mt-1 space-y-1">
                        {['Real-time GPS on active jobs','Map visibility and coordination'].map((t,i)=> (
                          <div key={`ob-ad-${i}`} className="flex items-start gap-2"><svg className="h-3 w-3 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ color:'#22c55e' }}><path d="M20 6L9 17l-5-5"/></svg><span>{t}</span></div>
                        ))}
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <button className="px-3 py-1 text-[12px] rounded w-full md:w-auto" style={{ background:'var(--fleet-accent)', color:'#000' }} disabled={planSubmitting=== (planCycle==='monthly'?'advanced_monthly':'advanced_yearly')} onClick={()=> subscribePlan(planCycle==='monthly'?'advanced_monthly':'advanced_yearly')}>{planSubmitting=== (planCycle==='monthly'?'advanced_monthly':'advanced_yearly') ? 'Loading…' : (planCycle==='monthly' ? 'Choose Advanced (Monthly)' : 'Choose Advanced (Yearly)')}</button>
                    </div>
                  </div>
                </div>
                <div className="p-4 border-t flex items-center justify-between" style={{ borderColor:'var(--fleet-border)' }}>
                  <div className="text-[11px]" style={{ color:'var(--fleet-muted)' }}>You can change or cancel anytime in Manage plan.</div>
                  <div className="flex items-center gap-2">
                    {planCycle==='monthly' && (
                      <button className="px-3 py-1 text-[12px] rounded border" style={{ border:'1px solid var(--fleet-border)', color:'var(--fleet-text)' }} onClick={()=>{ try{ localStorage.setItem('pricingOnboardDismissed','1'); } catch{} setShowPlanOnboard(false); }}>Continue as Free</button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* overlay driver pins removed; only moving badges on routes remain */}

        {/* driver popover with job details */}
        {activeDriver && activeDetail && (() => {
          const eta = activeEtaMin != null ? `${activeEtaMin} min` : '—';
          const driverName = activeDetail?.driverName || 'Driver';
          const jobType = (activeDetail?.jobType || '').toString();
          const jobId = activeDetail?.jobId;
          const descr = activeDetail?.description || '';
          const fromStr = (()=>{
            if (activeDetail?.from) return (typeof activeDetail.from==='string'? activeDetail.from : (activeDetail.from?.address || activeDetail.from?.formatted_address || [activeDetail.from?.line1, activeDetail.from?.city].filter(Boolean).join(', ')));
            return '—';
          })();
          const toStr = (()=>{
            if (activeDetail?.to) return (typeof activeDetail.to==='string'? activeDetail.to : (activeDetail.to?.address || activeDetail.to?.formatted_address || [activeDetail.to?.line1, activeDetail.to?.city].filter(Boolean).join(', ')));
            return '—';
          })();
          const isRecovery = (jobType || '').toUpperCase() === 'RECOVERY';
          const carMake = activeDetail?.carMake;
          const carModel = activeDetail?.carModel;
          const carReg = activeDetail?.carReg;
          const reason = activeDetail?.breakdownReason;
          return (
            <div className="absolute right-4 top-16 w-96 rounded-lg shadow-lg" style={{ background: 'var(--fleet-panel)', border: '1px solid var(--fleet-border)' }}>
              <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--fleet-border)' }}>
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full" style={{ background: '#22c55e' }} />
                  <div className="text-[13px] font-semibold" style={{ color: 'var(--fleet-text)' }}>{driverName}</div>
                  <div className="ml-auto text-[11px]" style={{ color: 'var(--fleet-muted)' }}>ETA {eta}</div>
                </div>
                <div className="text-[11px] mt-1" style={{ color: 'var(--fleet-muted)' }}>On {jobType} {jobId ? `• Job ${jobId}` : ''}</div>
              </div>
              <div className="p-4 space-y-2 text-[12px]" style={{ color: 'var(--fleet-text)' }}>
                {descr && <div>{descr}</div>}
                <div className="grid grid-cols-2 gap-3">
                  <div><span className="text-[11px]" style={{ color: 'var(--fleet-muted)' }}>Customer</span><br/>—</div>
                  <div><span className="text-[11px]" style={{ color: 'var(--fleet-muted)' }}>Price</span><br/>{activeDetail?.budget != null ? `£${Number(activeDetail.budget).toFixed(0)}` : '—'}</div>
                  <div><span className="text-[11px]" style={{ color: 'var(--fleet-muted)' }}>Pickup</span><br/>{fromStr}</div>
                  <div><span className="text-[11px]" style={{ color: 'var(--fleet-muted)' }}>Delivery</span><br/>{toStr}</div>
                </div>
                {isRecovery && (
                  <div className="grid grid-cols-2 gap-3">
                    <div><span className="text-[11px]" style={{ color: 'var(--fleet-muted)' }}>Vehicle</span><br/>{[carMake, carModel].filter(Boolean).join(' ') || '—'}</div>
                    <div><span className="text-[11px]" style={{ color: 'var(--fleet-muted)' }}>Reg</span><br/>{carReg || '—'}</div>
                    <div><span className="text-[11px]" style={{ color: 'var(--fleet-muted)' }}>Reason</span><br/>{reason || '—'}</div>
                  </div>
                )}
                <div className="flex items-center gap-2 text-[11px]" style={{ color: 'var(--fleet-muted)' }}>
                  {activeDetail?.isUrgent ? <span className="px-2 py-0.5 rounded bg-[#f59e0b] text-black">Urgent</span> : null}
                  {activeDetail?.fragile ? <span className="px-2 py-0.5 rounded bg-[#94a3b8] text-black">Fragile</span> : null}
                </div>
              </div>
              <div className="px-4 py-2 border-t flex justify-between items-center gap-2" style={{ borderColor: 'var(--fleet-border)' }}>
                <div className="flex items-center gap-2">
                  <button className="h-7 w-7 rounded-full flex items-center justify-center" title="Chat" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid var(--fleet-border)', color: 'var(--fleet-text)' }} onClick={async ()=>{
                    try {
                      const res = await fetch('/api/fleet/support/sessions/ensure', { method:'POST', headers:{ 'Content-Type':'application/json' }, body: JSON.stringify({ jobId: jobId, chatWith: 'driver', driverId: activeDriver }) });
                      const j = await res.json();
                      if (res.ok && j?.session?.id) {
                        try { localStorage.setItem('openSupportSessionId', j.session.id); } catch {}
                        router.push('/fleet/support');
                      } else { router.push('/fleet/support'); }
                    } catch { router.push('/fleet/support'); }
                  }}>
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><path d="M4 4h16v10H7l-3 3V4z"/></svg>
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <button className="px-2 py-1 text-[12px] rounded" style={{ background: 'var(--fleet-subtle)', border: '1px solid var(--fleet-border)', color: 'var(--fleet-text)' }} onClick={() => setActiveDriver(null)}>Close</button>
                  <button className="px-2 py-1 text-[12px] rounded" style={{ background: 'var(--fleet-accent)', color: '#000' }} disabled={!jobId} onClick={()=>{ if (jobId) router.push(`/fleet/jobs/${jobId}`); }}>View Details</button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Active drivers quick bar (wired) */}
        <div className="absolute left-4 top-4 w-80 rounded overflow-hidden pointer-events-auto" style={{ backgroundColor: 'var(--fleet-panel)', border: '1px solid var(--fleet-border)', zIndex: 20 }}>
          <button
            className="w-full px-3 py-2 text-[11px] uppercase tracking-wider flex items-center gap-2"
            style={{ color: 'var(--fleet-muted)', borderBottom: '1px solid var(--fleet-border)', textAlign: 'left' }}
            onClick={() => setShowActive((v) => !v)}
          >
            Active ({activeDrivers.length})
            <span className="ml-auto text-[11px] underline" style={{ color: 'var(--fleet-muted)' }}>{showActive ? 'Hide' : 'View'}</span>
          </button>
          {showActive && (
            <div className="px-3 py-2 space-y-1 text-[12px]" style={{ color:'var(--fleet-text)' }}>
              {activeDrivers.length===0 && (
                <div className="text-[11px]" style={{ color:'var(--fleet-muted)' }}>No drivers active</div>
              )}
              {activeDrivers.map((a)=> (
                <div key={a.driverId} className="rounded border p-2 flex items-center justify-between" style={{ borderColor:'var(--fleet-border)' }}>
                  <div>
                    <div className="font-medium">{a.driverName}</div>
                    <div className="text-[11px]" style={{ color:'var(--fleet-muted)' }}>{a.type} — {a.jobId}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="px-2 py-0.5 text-[11px] rounded border" style={{ borderColor:'var(--fleet-border)' }} onClick={()=> setActiveDriver(a.driverId)}>View</button>
                    <button className="px-2 py-0.5 text-[11px] rounded border" style={{ borderColor:'var(--fleet-border)' }} onClick={()=> router.push(`/fleet/jobs/${a.jobId}`)}>Job</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* active drivers panel (overlay) */}
        <div className="absolute left-4 top-4 w-80 rounded overflow-hidden pointer-events-none" style={{ backgroundColor: 'var(--fleet-panel)', border: '1px solid var(--fleet-border)' }}>
          <div style={{ pointerEvents: 'auto' }}>
          <button
            className="w-full px-3 py-2 text-[11px] uppercase tracking-wider flex items-center gap-2"
            style={{ color: 'var(--fleet-muted)', borderBottom: '1px solid var(--fleet-border)', textAlign: 'left' }}
            onClick={() => setShowActive((v) => !v)}
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
            
          >
            <span className="h-3 w-3 rounded-full" style={{ background: '#22c55e' }} />
            <span style={{ color: 'var(--fleet-text)' }}>Active</span>
            <span className="ml-auto text-[10px] px-2 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid var(--fleet-border)', color: 'var(--fleet-muted)' }}>
              {activeDrivers.length}
            </span>
            <span className="text-[10px]" style={{ color: 'var(--fleet-muted)' }}>{showActive ? 'Hide' : 'View'}</span>
          </button>
          {showActive && (
            <div className="max-h-80 overflow-auto p-2 space-y-2">
              {activeDrivers.map((a) => (
                <div key={`active-${a.driverId}`} className="rounded p-2 flex items-start gap-2" style={{ backgroundColor: 'var(--fleet-subtle)', border: '1px solid var(--fleet-border)' }}>
                  <div className="h-3 w-3 rounded-full mt-1" style={{ background: '#22c55e' }} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="text-[12px] font-medium" style={{ color: 'var(--fleet-text)' }}>{a.driverName}</div>
                      <div className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid var(--fleet-border)', color: 'var(--fleet-muted)' }}>{String(a.type).toUpperCase()}</div>
                      <div className="text-[10px]" style={{ color: 'var(--fleet-muted)' }}>Job {a.jobId}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
        </div>

        {/* footer rail */}
        <div className="absolute left-0 right-0 bottom-0 h-10 flex items-center gap-2 px-3" style={{ backgroundColor: 'rgba(14,17,20,0.9)', borderTop: '1px solid var(--fleet-border)' }}>
          {Array.from({ length: 9 }).map((_, i) => (
            <div key={i} className="h-5 w-20 rounded bg-zinc-500/20" />
          ))}
        </div>

        {/* right-side map controls (mock) */}
        <div className="absolute right-3 bottom-12 flex flex-col gap-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <button key={i} className="h-8 w-8 rounded bg-[#0e1114]/90 border border-white/10 text-zinc-400">{i===0?'+':i===1?'-':'⤢'}</button>
          ))}
        </div>
        {/* open live ops (Advanced) */}
        {(!subLoading && !!sub?.gpsTrackingEnabled) && (
          <div className="absolute right-3 top-3 z-10">
            <button
              className="px-3 py-1.5 text-[12px] rounded inline-flex items-center gap-2"
              style={{ background:'var(--fleet-panel)', border:'1px solid var(--fleet-border)', color:'var(--fleet-text)' }}
              onClick={()=> router.push('/fleet/live-ops')}
            >
              <MapIcon size={14} /> Open Live Ops
            </button>
          </div>
        )}

        {/* bulk drawer (center modal) */}
        {bulkOpen && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <div className="w-[520px] bg-[#0e1114] border border-white/10 rounded-lg shadow-xl">
              <div className="px-4 py-3 border-b border-white/10 text-[13px] text-zinc-300">Bulk Edit</div>
              <div className="p-4 space-y-3">
                {[
                  { label: 'Data 1' },
                  { label: 'Data 2' },
                  { label: 'Data 3' },
                ].map((f, idx) => (
                  <div key={idx} className="grid grid-cols-2 gap-2">
                    <select className="bg-[#12161a] border border-white/10 rounded px-2 py-1 text-[12px]">
                      <option>Current Data</option>
                    </select>
                    <select className="bg-[#12161a] border border-white/10 rounded px-2 py-1 text-[12px]">
                      <option>New Data</option>
                    </select>
                  </div>
                ))}
              </div>
              <div className="px-4 py-3 border-t border-white/10 flex justify-end gap-2">
                <button className="px-3 py-1 text-[12px] rounded bg-zinc-700/60" onClick={() => setBulkOpen(false)}>Cancel</button>
                <button className="px-3 py-1 text-[12px] rounded bg-[#0ea5e9] text-black" onClick={saveBulk}>Save</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}


